import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  IosShareOutlined as ExportIcon,
  FileUploadOutlined as ImportIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';

export default function ApplicationCodeManagement() {
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState('code');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [query, setQuery] = useState('');

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [editingId, setEditingId] = useState(null);

  function downloadTextFile({ filename, content, mime = 'text/plain;charset=utf-8' }) {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function downloadBlobFile({ filename, blob }) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function toCsvValue(v) {
    const s = String(v ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return `"${s.replaceAll('"', '""')}"`;
    }
    return s;
  }

  function parseCsvText(text) {
    const lines = String(text || '')
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    const out = [];
    for (const line of lines) {
      const cells = [];
      let cur = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (ch === '"') {
          const next = line[i + 1];
          if (inQuotes && next === '"') {
            cur += '"';
            i += 1;
          } else {
            inQuotes = !inQuotes;
          }
          continue;
        }
        if (ch === ',' && !inQuotes) {
          cells.push(cur.trim());
          cur = '';
          continue;
        }
        cur += ch;
      }
      cells.push(cur.trim());
      out.push(cells);
    }
    return out;
  }

  function normalizeHeaderCell(s) {
    return String(s || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/_/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  function looksLikeExcelFile(file) {
    const name = String(file?.name || '').toLowerCase();
    return name.endsWith('.xlsx') || name.endsWith('.xls');
  }

  async function parseExcelFileToRows(file) {
    const XLSX = await import('xlsx');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheetName = wb.SheetNames?.[0];
    const ws = wb.Sheets?.[sheetName];
    if (!ws) return [];
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    return Array.isArray(aoa) ? aoa : [];
  }

  const loadRows = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/application-codes', {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Failed to load', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.code, r.name, r.description, r.vehicleNumber, r.mobileNumber, r.status]
        .map((x) => String(x || '').toLowerCase())
        .some((s) => s.includes(q))
    );
  }, [rows, query]);

  const sortedRows = useMemo(() => {
    const stabilized = filteredRows.map((r, idx) => [r, idx]);
    const cmp = (a, b) => {
      const ra = a[0];
      const rb = b[0];
      const dir = order === 'asc' ? 1 : -1;
      const va = String(ra[orderBy] || '').toLowerCase();
      const vb = String(rb[orderBy] || '').toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return a[1] - b[1];
    };
    stabilized.sort(cmp);
    return stabilized.map((x) => x[0]);
  }, [filteredRows, order, orderBy]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOrderBy(col);
    setOrder('asc');
  };

  const openAdd = () => {
    setEditingId(null);
    window.location.href = '/applications/add';
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    window.location.href = `/applications/${row.id}/edit`;
  };

  // report page does not handle form save here; creation/edit handled on dedicated form page
  const exportCsv = () => {
    const header = [
      'Sr. No.',
      'Code',
      'Name',
      'Description',
      'Vehicle No.',
      'Mobile',
      'Status',
    ];
    const sorted = [...rows].sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')));
    const body = sorted
      .map((r, idx) =>
        [
          idx + 1,
          r.code,
          r.name,
          r.description,
          r.vehicleNumber || '',
          r.mobileNumber || '',
          r.status || '',
        ]
          .map(toCsvValue)
          .join(',')
      )
      .join('\n');
    downloadTextFile({
      filename: 'application_codes.csv',
      content: `${header.join(',')}\n${body}\n`,
      mime: 'text/csv;charset=utf-8',
    });
  };

  const exportExcel = async () => {
    const XLSX = await import('xlsx');
    const header = ['Sr. No.', 'Code', 'Name', 'Description', 'Vehicle No.', 'Mobile', 'Status'];
    const sorted = [...rows].sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')));
    const aoa = [
      header,
      ...sorted.map((r, idx) => [
        idx + 1,
        r.code,
        r.name,
        r.description,
        r.vehicleNumber || '',
        r.mobileNumber || '',
        r.status || '',
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'ApplicationCodes');
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlobFile({ filename: 'application_codes.xlsx', blob });
  };

  const triggerImport = () => {
    fileInputRef.current?.click?.();
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    try {
      const parsed = looksLikeExcelFile(file)
        ? await parseExcelFileToRows(file)
        : parseCsvText(await file.text());
      if (parsed.length === 0) throw new Error('Empty file');

      const header = parsed[0].map(normalizeHeaderCell);
      const body = parsed.slice(1);

      const idxSrNo = header.findIndex((h) => h === 'srno');
      const idxCode = header.findIndex((h) => h === 'code' || h === 'applicationcode');
      const idxName = header.findIndex((h) => h === 'name' || h === 'applicationname');
      const idxDesc = header.findIndex((h) => h === 'description' || h === 'desc');
      const idxVehicle = header.findIndex(
        (h) => h === 'vehicleno' || h === 'vehiclenumber' || h === 'vehicle'
      );
      const idxMobile = header.findIndex(
        (h) => h === 'mobile' || h === 'mobilenumber' || h === 'phone' || h === 'contact'
      );
      const idxStatus = header.findIndex((h) => h === 'status');

      if (idxCode === -1 || idxName === -1 || idxDesc === -1) {
        throw new Error('CSV headers must include: Code, Name, Description');
      }

      const incoming = [];
      for (const row of body) {
        const code = String(row[idxCode] ?? '').trim();
        const name = String(row[idxName] ?? '').trim();
        const description = String(row[idxDesc] ?? '').trim();
        const vehicleNumber = idxVehicle >= 0 ? String(row[idxVehicle] ?? '').trim() : '';
        const mobileNumber = idxMobile >= 0 ? String(row[idxMobile] ?? '').trim() : '';
        const statusRaw = idxStatus >= 0 ? String(row[idxStatus] ?? '').trim().toLowerCase() : 'active';
        const status = statusRaw === 'inactive' ? 'inactive' : 'active';
        if (!code || !name || !description) continue;

        incoming.push({
          code,
          name,
          description,
          vehicleNumber,
          mobileNumber,
          status,
        });
      }

      if (incoming.length === 0) throw new Error('No valid rows found in CSV');

      setLoading(true);
      const res = await fetch('/api/application-codes/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : '',
        },
        body: JSON.stringify({ items: incoming }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Import failed');

      await loadRows();
      setToast({
        open: true,
        message: `Import successful: ${data.inserted || 0} added, ${data.skipped || 0} skipped`,
        severity: 'success',
      });
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Import failed', severity: 'error' });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setLoading(false);
    }
  };

  const doDelete = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/application-codes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: token ? `Bearer ${token}` : '' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Delete failed');
      setRows((prev) => prev.filter((r) => r.id !== id));
      setToast({ open: true, message: 'Deleted', severity: 'info' });
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Delete failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Application Code Report">
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            mb: 3,
            overflow: 'hidden',
            borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#eef2ff'),
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" spacing={1.25}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 750 }}>Application Codes</Typography>
                <Chip size="small" label={rows.length === filteredRows.length ? `${rows.length} total` : `${filteredRows.length} filtered`} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(0);
                  }}
                  placeholder="Search"
                  sx={{ minWidth: 280 }}
                />
                <Button startIcon={<AddIcon />} variant="contained" onClick={openAdd} disabled={loading}>
                  New Code
                </Button>
                <Button
                  startIcon={<ExportIcon />}
                  variant="contained"
                  color="success"
                  onClick={exportCsv}
                  disabled={loading}
                >
                  Export CSV
                </Button>
                <Button
                  startIcon={<ExportIcon />}
                  variant="contained"
                  color="info"
                  onClick={exportExcel}
                  disabled={loading}
                >
                  Export Excel
                </Button>
                <Button
                  startIcon={<ImportIcon />}
                  variant="contained"
                  color="inherit"
                  onClick={triggerImport}
                  sx={{ bgcolor: 'grey.700', color: 'common.white', '&:hover': { bgcolor: 'grey.800' } }}
                  disabled={loading}
                >
                  Import
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  style={{ display: 'none' }}
                  onChange={(e) => handleImportFile(e.target.files?.[0])}
                />
              </Stack>
            </Stack>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>
                    <TableSortLabel active={orderBy === 'code'} direction={orderBy === 'code' ? order : 'asc'} onClick={() => handleSort('code')}>
                      Code
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Vehicle No.</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center" sx={{ width: 160 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r) => (
                  <TableRow key={r.id} hover sx={{ '& td': { py: 1.25, fontSize: 14 } }}>
                    <TableCell>
                      <Chip size="small" label={r.code} sx={{ borderRadius: 2, fontWeight: 800 }} />
                    </TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.vehicleNumber || '-'}</TableCell>
                    <TableCell>{r.mobileNumber || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.status}
                        sx={{
                          borderRadius: 2,
                          bgcolor: r.status === 'active' ? 'rgba(22,163,74,0.12)' : 'rgba(239,68,68,0.12)',
                          color: r.status === 'active' ? '#16a34a' : '#ef4444',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.75} justifyContent="center">
                        <Button size="small" onClick={() => openEdit(r)} startIcon={<EditIcon />} disabled={loading}>
                          Edit
                        </Button>
                        <Button size="small" color="error" onClick={() => doDelete(r.id)} startIcon={<DeleteIcon />} disabled={loading}>
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {pagedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 750 }}>
                          {query.trim() ? 'No matching codes' : 'No application codes yet'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mt: 0.75 }}>
                          {query.trim() ? 'Try a different search or clear the filter.' : 'Add your first code.'}
                        </Typography>
                        {!query.trim() && (
                          <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={openAdd}>
                            Add Code
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <TablePagination
            component="div"
            count={sortedRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      </Box>

      {/* Form is a dedicated page now */}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast((t) => ({ ...t, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
